import { useState } from "react";
import { toast } from "sonner";
import { Car, CheckCircle2, ArrowRight, ArrowLeft, Upload, TrendingUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { FileUpload } from "@/components/FileUpload";
import { IMAGES } from "@/lib/constants";
import { api, formatApiError } from "@/lib/api";
import { UkPhoneInput, isValidUkMobile } from "@/components/UkPhoneInput";

const SERVICES = ["Local Taxi", "Airport Transfer", "Station Transfer", "Outstation", "Tourist", "Corporate", "Executive", "Wheelchair Accessible", "Hourly Hire"];
const STEPS = ["Personal", "Business", "Vehicle", "Services", "Pricing", "Payout"];

export default function BecomeDriver() {
  const [started, setStarted] = useState(false);
  const [step, setStep] = useState(0);
  const [submitted, setSubmitted] = useState(null);
  const [personal, setPersonal] = useState({ name: "", phone: "", email: "", address: "" });
  const [business, setBusiness] = useState({ operator_name: "", crn: "", area: "", years: "" });
  const [vehicle, setVehicle] = useState({ reg: "", make: "", model: "", year: "", type: "", pax: "", color: "" });
  const [services, setServices] = useState([]);
  const [pricing, setPricing] = useState({ base_fare: "", per_mile: "" });
  const [bank, setBank] = useState({ account_name: "", sort_code: "", account_number: "" });
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);

  const toggleSvc = (s) => setServices((x) => x.includes(s) ? x.filter((y) => y !== s) : [...x, s]);

  const submit = async () => {
    if (!isValidUkMobile(personal.phone)) { toast.error("Please enter a valid UK mobile number"); setStep(0); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/driver-applications", { personal, business, vehicle, services, pricing, bank, documents });
      setSubmitted(data);
      toast.success("Application submitted!");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Could not submit application"); }
    finally { setLoading(false); }
  };

  if (submitted) {
    return (
      <div className="mx-auto max-w-lg px-4 py-20 text-center">
        <CheckCircle2 size={64} className="mx-auto text-emerald-500" />
        <h1 className="mt-4 font-heading text-3xl font-bold text-navy">Application Submitted</h1>
        <p className="mt-2 text-slate-500">Thank you for applying to become a SmartFares4U partner.</p>
        <div className="mt-4 inline-block rounded-full bg-brand-light px-4 py-1.5 font-heading font-bold text-brand">{submitted.application_id}</div>
        <p className="mt-4 text-sm text-slate-500">Status: <span className="font-semibold text-amber-600">Under Review</span></p>
      </div>
    );
  }

  if (!started) {
    return (
      <div>
        <section className="relative bg-navy">
          <img src={IMAGES.executiveDriver} alt="" className="absolute inset-0 h-full w-full object-cover opacity-30" />
          <div className="absolute inset-0 bg-navy/65" />
          <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
            <div className="max-w-xl">
              <span className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-xs font-medium text-white"><Car size={14} className="text-brand" /> Become a SmartFares4U Partner</span>
              <h1 className="mt-4 font-heading text-4xl font-bold text-white lg:text-5xl">Grow Your Taxi Business With SmartFares4U</h1>
              <p className="mt-4 text-slate-300">More bookings, airport opportunities, long-distance journeys, business customers and a central dashboard to manage it all.</p>
              <Button size="lg" className="mt-6 bg-brand hover:bg-brand/90" onClick={() => setStarted(true)} data-testid="apply-now-btn">Apply Now</Button>
            </div>
          </div>
        </section>
        <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {["More bookings", "Digital booking management", "Airport opportunities", "Central dashboard"].map((b) => (
            <div key={b} className="rounded-2xl border border-slate-200 bg-white p-6"><TrendingUp size={22} className="text-brand" /><h3 className="mt-3 font-heading font-semibold text-navy">{b}</h3></div>
          ))}
        </section>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-2xl px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-6 flex items-center gap-1 overflow-x-auto no-scrollbar">
        {STEPS.map((s, i) => (
          <div key={s} className="flex shrink-0 items-center gap-1">
            <div className={`flex h-8 w-8 items-center justify-center rounded-full text-xs font-semibold ${i <= step ? "bg-brand text-white" : "bg-slate-200 text-slate-500"}`}>{i < step ? <CheckCircle2 size={14} /> : i + 1}</div>
            <span className={`text-xs font-medium ${i <= step ? "text-navy" : "text-slate-400"}`}>{s}</span>
            {i < STEPS.length - 1 && <div className="h-px w-3 bg-slate-200" />}
          </div>
        ))}
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-6">
        {step === 0 && <Grid>
          <F label="Full Name" v={personal.name} on={(e) => setPersonal({ ...personal, name: e.target.value })} t="da-name" />
          <div>
            <Label>UK Mobile</Label>
            <UkPhoneInput value={personal.phone} onChange={(e) => setPersonal({ ...personal, phone: e.target.value })} invalid={!!personal.phone && !isValidUkMobile(personal.phone)} data-testid="da-phone" required />
          </div>
          <F label="Email" v={personal.email} on={(e) => setPersonal({ ...personal, email: e.target.value })} t="da-email" />
          <F label="Address" v={personal.address} on={(e) => setPersonal({ ...personal, address: e.target.value })} t="da-address" full />
        </Grid>}
        {step === 1 && <Grid>
          <F label="Business / Operator Name" v={business.operator_name} on={(e) => setBusiness({ ...business, operator_name: e.target.value })} t="da-op" full />
          <F label="Company Registration No." v={business.crn} on={(e) => setBusiness({ ...business, crn: e.target.value })} t="da-crn" />
          <F label="Operating Area" v={business.area} on={(e) => setBusiness({ ...business, area: e.target.value })} t="da-area" />
          <F label="Years Operating" v={business.years} on={(e) => setBusiness({ ...business, years: e.target.value })} t="da-years" />
        </Grid>}
        {step === 2 && <>
          <Grid>
            <F label="Registration Number" v={vehicle.reg} on={(e) => setVehicle({ ...vehicle, reg: e.target.value })} t="da-reg" />
            <F label="Make" v={vehicle.make} on={(e) => setVehicle({ ...vehicle, make: e.target.value })} t="da-make" />
            <F label="Model" v={vehicle.model} on={(e) => setVehicle({ ...vehicle, model: e.target.value })} t="da-model" />
            <F label="Year" v={vehicle.year} on={(e) => setVehicle({ ...vehicle, year: e.target.value })} t="da-year" />
            <F label="Vehicle Type" v={vehicle.type} on={(e) => setVehicle({ ...vehicle, type: e.target.value })} t="da-type" />
            <F label="Passenger Capacity" v={vehicle.pax} on={(e) => setVehicle({ ...vehicle, pax: e.target.value })} t="da-pax" />
          </Grid>
          <div className="mt-4 space-y-3">
            <FileUpload docType="vehicle_photo" label="Vehicle photos" files={documents.filter((d) => d.doc_type === "vehicle_photo")} onUploaded={(f) => setDocuments((x) => [...x, f])} onRemove={(id) => setDocuments((x) => x.filter((d) => d.id !== id))} />
            <FileUpload docType="licence" label="Driving / private-hire licence" files={documents.filter((d) => d.doc_type === "licence")} onUploaded={(f) => setDocuments((x) => [...x, f])} onRemove={(id) => setDocuments((x) => x.filter((d) => d.id !== id))} />
            <FileUpload docType="insurance" label="Insurance document" files={documents.filter((d) => d.doc_type === "insurance")} onUploaded={(f) => setDocuments((x) => [...x, f])} onRemove={(id) => setDocuments((x) => x.filter((d) => d.id !== id))} />
          </div>
        </>}
        {step === 3 && <div className="grid gap-2 sm:grid-cols-2">
          {SERVICES.map((s) => (
            <label key={s} className="flex cursor-pointer items-center gap-3 rounded-xl border border-slate-200 p-3 hover:bg-slate-50">
              <Checkbox checked={services.includes(s)} onCheckedChange={() => toggleSvc(s)} data-testid={`da-svc-${s.replace(/[^a-z]/gi, "").toLowerCase()}`} /> <span className="text-sm text-slate-700">{s}</span>
            </label>
          ))}
        </div>}
        {step === 4 && <Grid>
          <F label="Base Fare (£)" v={pricing.base_fare} on={(e) => setPricing({ ...pricing, base_fare: e.target.value })} t="da-basefare" />
          <F label="Per Mile (£)" v={pricing.per_mile} on={(e) => setPricing({ ...pricing, per_mile: e.target.value })} t="da-permile" />
        </Grid>}
        {step === 5 && <Grid>
          <F label="Account Name" v={bank.account_name} on={(e) => setBank({ ...bank, account_name: e.target.value })} t="da-acname" full />
          <F label="Sort Code" v={bank.sort_code} on={(e) => setBank({ ...bank, sort_code: e.target.value })} t="da-sort" />
          <F label="Account Number" v={bank.account_number} on={(e) => setBank({ ...bank, account_number: e.target.value })} t="da-acnum" />
        </Grid>}

        <div className="mt-6 flex gap-2">
          {step > 0 && <Button variant="outline" onClick={() => setStep(step - 1)} className="gap-1"><ArrowLeft size={16} /> Back</Button>}
          {step < STEPS.length - 1 ? (
            <Button className="flex-1 gap-2 bg-brand hover:bg-brand/90" onClick={() => setStep(step + 1)} data-testid="da-next">Continue <ArrowRight size={16} /></Button>
          ) : (
            <Button className="flex-1 bg-brand hover:bg-brand/90" onClick={submit} disabled={loading} data-testid="da-submit">{loading ? "Submitting…" : "Submit Application"}</Button>
          )}
        </div>
      </div>
    </div>
  );
}
function Grid({ children }) { return <div className="grid gap-3 sm:grid-cols-2">{children}</div>; }
function F({ label, v, on, t, full }) { return <div className={full ? "sm:col-span-2" : ""}><Label>{label}</Label><Input value={v} onChange={on} data-testid={t} /></div>; }
